import { useEffect, useRef, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { HoverHelp } from '@/tableControls'
import { BARE_TRIGGER } from './gridClasses'

type Props = {
  saveLabel: string
  saveEnabled: boolean
  saving: boolean
  saveHelpText: string
  caseActionsDisabled?: boolean
  discardEnabled?: boolean
  caseEditable: boolean
  caseExpired: boolean
  recipeLineCount: number
  holdEnabled: boolean
  summaryEnabled: boolean
  summaryDisabledTitle?: string
  exportEnabled: boolean
  exportDisabledTitle?: string
  exporting: boolean
  onSave: () => void
  onEditCase: () => void
  onDuplicate: () => void
  onDiscard: () => void
  onDelete: () => void
  onSummary: () => void
  onExport: () => void
  onReanchorRecipes: () => void
  onHoldProjectedValues: () => void
}

function MenuItemWithHelp({
  label,
  disabled,
  help,
  onClick,
}: {
  label: string
  disabled: boolean
  help?: string
  onClick: () => void
}) {
  const button = (
    <button
      type="button"
      role="menuitem"
      className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </button>
  )
  if (disabled && help) {
    return (
      <span className={BARE_TRIGGER}>
        <HoverHelp aria-label={label} label={button}>
          {help}
        </HoverHelp>
      </span>
    )
  }
  return button
}

function MenuDivider() {
  return <div className="my-1 border-t border-gray-100" role="separator" />
}

export default function ScenarioSaveSplitButton({
  saveLabel,
  saveEnabled,
  saving,
  saveHelpText,
  caseActionsDisabled = false,
  discardEnabled = false,
  caseEditable,
  caseExpired,
  recipeLineCount,
  holdEnabled,
  summaryEnabled,
  summaryDisabledTitle,
  exportEnabled,
  exportDisabledTitle,
  exporting,
  onSave,
  onEditCase,
  onDuplicate,
  onDiscard,
  onDelete,
  onSummary,
  onExport,
  onReanchorRecipes,
  onHoldProjectedValues,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return
      setMenuOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  const saveDisabled = !saveEnabled || saving
  const reanchorDisabled = recipeLineCount === 0 || !caseEditable || caseExpired
  const reanchorHelp =
    recipeLineCount === 0
      ? 'No recipe-driven lines on this case'
      : !caseEditable
        ? 'Case is not editable'
        : caseExpired
          ? 'Expired cases cannot be re-anchored'
          : undefined
  const exportDisabled = !exportEnabled || exporting
  const exportHelp =
    exportDisabled && exportDisabledTitle
      ? exportDisabledTitle
      : exportDisabled
        ? 'Export projected values to the CGM Google Sheet'
        : undefined
  const summaryDisabled = !summaryEnabled
  const summaryHelp =
    summaryDisabled && summaryDisabledTitle
      ? summaryDisabledTitle
      : summaryDisabled
        ? 'Save the case to generate a summary'
        : undefined

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={[
          'flex h-8 shrink-0 items-stretch overflow-hidden rounded-lg text-xs font-medium text-white shadow-sm',
          '[background:var(--gradient-primary)]',
        ].join(' ')}
      >
        <span className={BARE_TRIGGER}>
          <HoverHelp aria-label="Save help" label={
            <button
              type="button"
              onClick={onSave}
              disabled={saveDisabled}
              className="inline-flex h-full items-center px-3 hover:bg-black/10 disabled:cursor-not-allowed disabled:bg-white/50 disabled:text-white/50 disabled:hover:bg-white/50"
            >
              {saveLabel}
            </button>
          }>
            {saveHelpText}
          </HoverHelp>
        </span>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="More scenario actions"
          className="flex w-8 items-center justify-center border-l border-white/25 hover:bg-black/10"
          onClick={(event) => {
            event.stopPropagation()
            setMenuOpen((open) => !open)
          }}
        >
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      </div>
      {menuOpen ? (
        <div
          role="menu"
          className="absolute left-0 z-30 mt-1 min-w-[16rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
            disabled={!discardEnabled}
            onClick={() => {
              setMenuOpen(false)
              onDiscard()
            }}
          >
            Discard changes
          </button>
          <MenuDivider />
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
            disabled={caseActionsDisabled}
            onClick={() => {
              setMenuOpen(false)
              onEditCase()
            }}
          >
            Edit case…
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
            disabled={caseActionsDisabled}
            onClick={() => {
              setMenuOpen(false)
              onDuplicate()
            }}
          >
            Duplicate case…
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
            disabled={caseActionsDisabled}
            onClick={() => {
              setMenuOpen(false)
              onDelete()
            }}
          >
            Delete case…
          </button>
          <MenuDivider />
          <MenuItemWithHelp
            label="Case summary…"
            disabled={summaryDisabled}
            help={summaryHelp}
            onClick={() => {
              if (summaryDisabled) return
              setMenuOpen(false)
              onSummary()
            }}
          />
          <MenuItemWithHelp
            label="Export to CGM…"
            disabled={exportDisabled}
            help={exportHelp}
            onClick={() => {
              if (exportDisabled) return
              setMenuOpen(false)
              onExport()
            }}
          />
          <MenuDivider />
          <MenuItemWithHelp
            label="Re-anchor recipes to latest actuals…"
            disabled={reanchorDisabled}
            help={reanchorHelp}
            onClick={() => {
              if (reanchorDisabled) return
              setMenuOpen(false)
              onReanchorRecipes()
            }}
          />
          <MenuItemWithHelp
            label="Hold projected values from before the roll-forward…"
            disabled={!holdEnabled}
            help={!holdEnabled ? 'No pre-roll values available on this case' : undefined}
            onClick={() => {
              if (!holdEnabled) return
              setMenuOpen(false)
              onHoldProjectedValues()
            }}
          />
        </div>
      ) : null}
    </div>
  )
}
